<template>
  <div class="text-center">
    <button
      type="button"
      @click="hop"
      class="caught-up-note block mx-auto mb-2 focus:outline-none"
      :class="{ hopping }"
      aria-label="Play a little tune"
    >
      <svg viewBox="0 0 100 100" class="h-20 w-20" aria-hidden="true">
        <g class="zzz fill-fg-faint dark:fill-fg-faint-dark" font-weight="600" font-family="Inter, system-ui, sans-serif">
          <text x="70" y="30" font-size="11">z</text>
          <text x="78" y="20" font-size="9">z</text>
          <text x="85" y="12" font-size="7">z</text>
        </g>
        <g class="body text-indigo-400 dark:text-indigo-300">
          <rect x="50" y="18" width="4.5" height="54" rx="2.25" fill="currentColor" />
          <path d="M54 18 C66 26 75 35 70 52 C68 42 62 35 54 32 Z" fill="currentColor" />
          <ellipse cx="38" cy="74" rx="15" ry="11" transform="rotate(-20 38 74)" fill="currentColor" />
          <g class="stroke-surface dark:stroke-surface-dark" stroke-width="1.8" stroke-linecap="round" fill="none">
            <path d="M28.5 75 q2.5 2 5 0" />
            <path d="M38.5 71.5 q2.5 2 5 0" />
            <path d="M35 80 q2 1.2 4 -0.6" />
          </g>
        </g>
      </svg>
    </button>
    <p class="text-fg-secondary dark:text-fg-secondary-dark font-medium">All caught up</p>
    <p class="text-sm text-fg-faint dark:text-fg-faint-dark mt-0.5">{{ line }}</p>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const LINES = [
  'Take a rest — you\'ve earned it.',
  'A quiet measure. Enjoy the fermata.',
  'Every student has found their teacher.',
  'Nothing open. Time to practice?',
]
const line = LINES[Math.floor(Math.random() * LINES.length)]

const hopping = ref(false)

// Click the note → it hops and plays a soft C-major arpeggio.
const hop = () => {
  if (hopping.value) return
  hopping.value = true
  setTimeout(() => { hopping.value = false }, 700)
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    ;[523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      const t = ctx.currentTime + i * 0.11
      osc.type = 'triangle'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(0.08, t + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35)
      osc.connect(gain).connect(ctx.destination)
      osc.start(t)
      osc.stop(t + 0.4)
    })
    setTimeout(() => ctx.close(), 1200)
  } catch { /* no audio — the hop alone is fine */ }
}
</script>

<style scoped>
.body {
  transform-box: fill-box;
  transform-origin: 50% 90%;
  animation: breathe 3.2s ease-in-out infinite;
}
.zzz text {
  opacity: 0;
  animation: drift 3.2s ease-in-out infinite;
}
.zzz text:nth-child(2) { animation-delay: 0.5s; }
.zzz text:nth-child(3) { animation-delay: 1s; }

.hopping .body { animation: hop 0.7s cubic-bezier(.3, 1.6, .5, 1); }
.hopping .zzz { visibility: hidden; }

@keyframes breathe {
  0%, 100% { transform: translateY(0) scale(1, 1); }
  50%      { transform: translateY(-1.5px) scale(1.02, 0.98); }
}
@keyframes drift {
  0%   { opacity: 0; transform: translate(0, 4px); }
  30%  { opacity: 1; }
  80%  { opacity: 0.6; }
  100% { opacity: 0; transform: translate(3px, -6px); }
}
@keyframes hop {
  0%   { transform: translateY(0) rotate(0); }
  35%  { transform: translateY(-14px) rotate(-8deg); }
  70%  { transform: translateY(0) rotate(4deg) scale(1.05, 0.95); }
  100% { transform: translateY(0) rotate(0); }
}

@media (prefers-reduced-motion: reduce) {
  .body, .zzz text { animation: none; }
  .zzz text { opacity: 0.7; }
  .hopping .body { animation: none; }
}
</style>
